import { Reveal } from "./Reveal";
import { COACHES, IMAGES } from "../../data";

function CoachProfile({ coach, reverse, index }) {
  return (
    <article
      data-testid={`coach-${coach.slug}`}
      className={`relative grid lg:grid-cols-12 gap-6 lg:gap-14 items-stretch`}
    >
      {/* Photo */}
      <Reveal
        className={`lg:col-span-5 order-1 ${
          reverse ? "lg:order-2" : "lg:order-1"
        }`}
        delay={0.05}
      >
        <div className="relative overflow-hidden rounded-lg border border-white/10 bg-[#0E0E0E] aspect-[4/5] lg:aspect-[3/4] h-full">
          <img
            src={IMAGES[coach.img]}
            alt={coach.name}
            data-testid={`coach-${coach.slug}-photo`}
            className="absolute inset-0 h-full w-full object-cover object-top"
            style={{ imageOrientation: "from-image" }}
          />
          {/* Subtle edge fade into dark background */}
          <div
            className="pointer-events-none absolute inset-0"
            style={{
              background: reverse
                ? "linear-gradient(to left, rgba(10,10,10,0.55) 0%, rgba(10,10,10,0) 45%)"
                : "linear-gradient(to right, rgba(10,10,10,0.55) 0%, rgba(10,10,10,0) 45%)",
            }}
          />
          <div className="absolute inset-x-0 bottom-0 h-32 bg-gradient-to-t from-ink/70 via-ink/10 to-transparent" />
          <div className="absolute left-0 top-0 h-1 w-20 bg-hotpink" />
          <div className="absolute right-4 top-4 font-body text-[10px] tracking-[0.35em] text-white/60 uppercase">
            /  0{index + 1}
          </div>
        </div>
      </Reveal>

      {/* Content */}
      <Reveal
        className={`lg:col-span-7 order-2 ${
          reverse ? "lg:order-1" : "lg:order-2"
        }`}
        delay={0.12}
      >
        <div className="h-full flex flex-col justify-center lg:py-6">
          <p className="font-body text-[11px] md:text-xs uppercase tracking-[0.3em] text-teal">
            {coach.eyebrow}
          </p>
          <h3
            className="mt-5 font-display uppercase leading-[0.9] tracking-tighter text-white text-4xl md:text-5xl lg:text-6xl"
            data-testid={`coach-${coach.slug}-name`}
          >
            {coach.name}
          </h3>

          <div className="mt-6 flex flex-wrap items-center gap-3">
            <span
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-hotpink/40 bg-hotpink/10"
              data-testid={`coach-${coach.slug}-experience`}
            >
              <span className="h-1.5 w-1.5 rounded-full bg-hotpink" />
              <span className="font-body text-xs uppercase tracking-[0.22em] text-white">
                {coach.experience}
              </span>
            </span>
          </div>

          <p className="mt-4 font-body text-[11px] md:text-xs uppercase tracking-[0.22em] text-neutral-400">
            {coach.lineage}
          </p>

          <div className="mt-8 max-w-[62ch] space-y-5 font-body text-base md:text-lg text-neutral-300 leading-relaxed">
            {coach.bio.map((para, i) => (
              <p key={i}>{para}</p>
            ))}
          </div>
        </div>
      </Reveal>

      {/* Divider bar under content area on lg */}
      <div className="lg:col-span-12 order-3 h-px w-full bg-white/5 lg:hidden mt-2" />
    </article>
  );
}

export default function Coaches() {
  return (
    <section
      id="coaches"
      className="relative bg-ink py-24 md:py-36 px-6 lg:px-10 overflow-hidden"
      data-testid="coaches-section"
    >
      {/* Subtle brand glow */}
      <div className="pointer-events-none absolute -top-24 left-1/2 -translate-x-1/2 h-[500px] w-[500px] rounded-full bg-hotpink/[0.06] blur-[140px]" />

      <div className="relative mx-auto max-w-[1400px]">
        <Reveal>
          <p className="font-body text-xs md:text-sm uppercase tracking-[0.3em] text-teal mb-6">
            / The Corner
          </p>
        </Reveal>
        <Reveal delay={0.1}>
          <h2 className="font-display font-700 uppercase leading-[0.9] tracking-tighter text-white text-5xl md:text-6xl lg:text-7xl mb-6">
            Coached by
          </h2>
        </Reveal>
        <Reveal delay={0.15}>
          <p className="max-w-2xl font-body text-base md:text-lg text-neutral-400 mb-16 md:mb-24 leading-relaxed">
            Two coaches. One standard. Real competitive experience,
            technical development, and a room built for the people who walk
            through the doors.
          </p>
        </Reveal>

        <div className="space-y-24 md:space-y-32">
          {COACHES.map((coach, i) => (
            <CoachProfile
              key={coach.slug}
              coach={coach}
              reverse={i % 2 === 1}
              index={i}
            />
          ))}
        </div>
      </div>
    </section>
  );
}
